import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { Zap, Plus, Trash2, Edit, Clock, ArrowRight, Bell, User, MessageSquare } from 'lucide-react';

const API = `${API_BASE_URL}/api`;

const AutomationsTab = ({ boardId, workspaceId, boardType }) => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);

  // New automation state
  const [automationName, setAutomationName] = useState('');
  const [automationDescription, setAutomationDescription] = useState('');
  const [triggerType, setTriggerType] = useState('status_change');
  const [triggerField, setTriggerField] = useState('');
  const [triggerValue, setTriggerValue] = useState('');
  const [triggerComparison, setTriggerComparison] = useState('equals');
  const [triggerDays, setTriggerDays] = useState('');
  const [actions, setActions] = useState([{ type: 'send_notification', field: '', value: '', message: '' }]);

  useEffect(() => {
    fetchAutomations();
  }, [boardId]);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/boards/${boardId}/automations?workspace_id=${workspaceId}`, { withCredentials: true });
      setAutomations(response.data);
    } catch (error) {
      console.error('Error fetching automations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAutomation = async () => {
    try {
      const automationData = {
        name: automationName,
        description: automationDescription,
        trigger: {
          type: triggerType,
          field: triggerField || undefined,
          value: triggerValue || undefined,
          comparison: triggerComparison || undefined,
          days: triggerDays ? parseInt(triggerDays) : undefined
        },
        actions: actions.map(action => ({
          type: action.type,
          field: action.field || undefined,
          value: action.value || undefined,
          message: action.message || undefined
        })),
        is_active: true
      };

      await axios.post(`${API}/boards/${boardId}/automations?workspace_id=${workspaceId}`, automationData, { withCredentials: true });
      
      // Reset form
      resetForm();
      setShowCreateDialog(false);
      fetchAutomations();
    } catch (error) {
      console.error('Error creating automation:', error);
      alert('Error creating automation');
    }
  };

  const handleToggleAutomation = async (automationId, currentState) => {
    try {
      await axios.put(`${API}/automations/${automationId}`, { is_active: !currentState }, { withCredentials: true });
      fetchAutomations();
    } catch (error) {
      console.error('Error toggling automation:', error);
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    if (!window.confirm('Are you sure you want to delete this automation?')) return;
    
    try {
      await axios.delete(`${API}/automations/${automationId}`, { withCredentials: true });
      fetchAutomations();
    } catch (error) {
      console.error('Error deleting automation:', error);
    }
  };

  const resetForm = () => {
    setAutomationName('');
    setAutomationDescription('');
    setTriggerType('status_change');
    setTriggerField('');
    setTriggerValue('');
    setTriggerComparison('equals');
    setTriggerDays('');
    setActions([{ type: 'send_notification', field: '', value: '', message: '' }]);
    setEditingAutomation(null);
  };

  const addAction = () => {
    setActions([...actions, { type: 'send_notification', field: '', value: '', message: '' }]);
  };

  const updateAction = (index, field, value) => {
    const newActions = [...actions];
    newActions[index][field] = value;
    setActions(newActions);
  };

  const removeAction = (index) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  const getTemplates = () => {
    const templates = {
      project: [
        {
          name: "Progress Complete → Review",
          description: "Move task to Review when progress reaches 100%",
          trigger: { type: "progress_reaches", value: "100" },
          actions: [{ type: "change_status", value: "review" }]
        },
        {
          name: "Overdue Task Alert",
          description: "Notify assignee when task is 1 day overdue",
          trigger: { type: "overdue", days: 1 },
          actions: [{ type: "send_notification", message: "This task is overdue!" }]
        }
      ],
      marketing: [
        {
          name: "Launch Date Trigger",
          description: "Change status to Live when launch date arrives",
          trigger: { type: "date_arrives", field: "date" },
          actions: [{ type: "change_status", value: "live" }]
        },
        {
          name: "Campaign Analysis Reminder",
          description: "Add comment after 7 days to analyze performance",
          trigger: { type: "date_arrives", field: "date" },
          actions: [{ type: "add_comment", message: "Time to analyze campaign performance!" }]
        }
      ],
      sales: [
        {
          name: "Stale Deal Alert",
          description: "Notify when deal has no activity for 14 days",
          trigger: { type: "overdue", days: 14 },
          actions: [{ type: "send_notification", message: "This deal needs attention!" }]
        },
        {
          name: "High Value Deal Notification",
          description: "Notify team lead when deal value exceeds €50,000",
          trigger: { type: "field_change", field: "deal_value", comparison: "greater_than", value: "50000" },
          actions: [{ type: "send_notification", message: "High value deal alert!" }]
        }
      ]
    };

    return templates[boardType] || templates.project;
  };

  const applyTemplate = (template) => {
    setAutomationName(template.name);
    setAutomationDescription(template.description);
    setTriggerType(template.trigger.type);
    setTriggerField(template.trigger.field || '');
    setTriggerValue(template.trigger.value || '');
    setTriggerComparison(template.trigger.comparison || 'equals');
    setTriggerDays(template.trigger.days?.toString() || '');
    setActions(template.actions);
    setShowCreateDialog(true);
  };

  const getTriggerIcon = (type) => {
    const icons = {
      status_change: <Zap className="h-4 w-4" />,
      date_arrives: <Clock className="h-4 w-4" />,
      field_change: <Edit className="h-4 w-4" />,
      overdue: <Clock className="h-4 w-4" />,
      progress_reaches: <Edit className="h-4 w-4" />
    };
    return icons[type] || <Zap className="h-4 w-4" />;
  };

  const getActionIcon = (type) => {
    const icons = {
      send_notification: <Bell className="h-4 w-4" />,
      change_status: <Edit className="h-4 w-4" />,
      assign_to: <User className="h-4 w-4" />,
      update_field: <Edit className="h-4 w-4" />,
      add_comment: <MessageSquare className="h-4 w-4" />
    };
    return icons[type] || <Zap className="h-4 w-4" />;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automations</h2>
          <p className="text-sm text-gray-600 mt-1">Set up workflows to automate repetitive tasks</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-[#1C3E53] hover:bg-[#2A5470]">
          <Plus className="h-4 w-4 mr-2" />
          Create Automation
        </Button>
      </div>

      {/* Templates Section */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Templates</h3>
        <div className="grid grid-cols-2 gap-3">
          {getTemplates().map((template, index) => (
            <Card 
              key={index}
              className="p-4 cursor-pointer hover:border-[#1C3E53] transition-colors"
              onClick={() => applyTemplate(template)}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Zap className="h-4 w-4 text-[#1C3E53]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm text-gray-900">{template.name}</h4>
                  <p className="text-xs text-gray-600 mt-1">{template.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Active Automations */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Active Automations ({automations.filter(a => a.is_active).length})
        </h3>
        
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading automations...</div>
        ) : automations.length === 0 ? (
          <Card className="p-8 text-center">
            <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No automations yet</p>
            <p className="text-sm text-gray-500 mt-2">Create your first automation to get started</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {automations.map((automation) => (
              <Card key={automation._id} className={`p-4 ${!automation.is_active ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Switch
                        checked={automation.is_active}
                        onCheckedChange={() => handleToggleAutomation(automation._id, automation.is_active)}
                      />
                      <h4 className="font-semibold text-gray-900">{automation.name}</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{automation.description}</p>
                    
                    <div className="flex items-center gap-2 text-xs">
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded">
                        {getTriggerIcon(automation.trigger.type)}
                        <span className="text-blue-700">
                          When: {automation.trigger.type.replace('_', ' ')}
                        </span>
                      </div>
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                      <div className="flex items-center gap-1">
                        {automation.actions.map((action, idx) => (
                          <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded">
                            {getActionIcon(action.type)}
                            <span className="text-green-700">
                              {action.type.replace('_', ' ')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAutomation(automation._id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Automation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Automation</DialogTitle>
            <DialogDescription>
              Set up a "When → Then" workflow to automate your board
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Automation Name</Label>
                <Input
                  placeholder="e.g., Notify on overdue tasks"
                  value={automationName}
                  onChange={(e) => setAutomationName(e.target.value)}
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  placeholder="What does this automation do?"
                  value={automationDescription}
                  onChange={(e) => setAutomationDescription(e.target.value)}
                />
              </div>
            </div>

            {/* Trigger Configuration */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#1C3E53]" />
                WHEN (Trigger)
              </h3>
              
              <div className="space-y-3">
                <div>
                  <Label>Trigger Type</Label>
                  <Select value={triggerType} onValueChange={setTriggerType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="status_change">Status changes to</SelectItem>
                      <SelectItem value="date_arrives">Date arrives</SelectItem>
                      <SelectItem value="field_change">Field changes</SelectItem>
                      <SelectItem value="overdue">Task is overdue</SelectItem>
                      <SelectItem value="progress_reaches">Progress reaches</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {triggerType === 'status_change' && (
                  <div>
                    <Label>Status Value</Label>
                    <Input
                      placeholder="e.g., done, review"
                      value={triggerValue}
                      onChange={(e) => setTriggerValue(e.target.value)}
                    />
                  </div>
                )}

                {triggerType === 'date_arrives' && (
                  <div>
                    <Label>Date Field</Label>
                    <Select value={triggerField} onValueChange={setTriggerField}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date">Task Date</SelectItem>
                        <SelectItem value="closing_date">Closing Date</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {triggerType === 'field_change' && (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Field</Label>
                      <Input
                        placeholder="e.g., deal_value"
                        value={triggerField}
                        onChange={(e) => setTriggerField(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Comparison</Label>
                      <Select value={triggerComparison} onValueChange={setTriggerComparison}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="greater_than">Greater than</SelectItem>
                          <SelectItem value="less_than">Less than</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Value</Label>
                      <Input
                        placeholder="Target value"
                        value={triggerValue}
                        onChange={(e) => setTriggerValue(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {triggerType === 'overdue' && (
                  <div>
                    <Label>Days Overdue</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 1, 7, 14"
                      value={triggerDays}
                      onChange={(e) => setTriggerDays(e.target.value)}
                    />
                  </div>
                )}

                {triggerType === 'progress_reaches' && (
                  <div>
                    <Label>Progress (%)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      value={triggerValue}
                      onChange={(e) => setTriggerValue(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Actions Configuration */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-[#1C3E53]" />
                  THEN (Actions)
                </h3>
                <Button variant="outline" size="sm" onClick={addAction}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Action
                </Button>
              </div>

              <div className="space-y-3">
                {actions.map((action, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 space-y-3">
                        <Select 
                          value={action.type} 
                          onValueChange={(value) => updateAction(index, 'type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="send_notification">Send Notification</SelectItem>
                            <SelectItem value="change_status">Change Status</SelectItem>
                            <SelectItem value="assign_to">Assign To</SelectItem>
                            <SelectItem value="update_field">Update Field</SelectItem>
                            <SelectItem value="add_comment">Add Comment</SelectItem>
                          </SelectContent>
                        </Select>

                        {action.type === 'send_notification' && (
                          <Input
                            placeholder="Notification message"
                            value={action.message}
                            onChange={(e) => updateAction(index, 'message', e.target.value)}
                          />
                        )}

                        {action.type === 'change_status' && (
                          <Input
                            placeholder="New status value (e.g., done, review)"
                            value={action.value}
                            onChange={(e) => updateAction(index, 'value', e.target.value)}
                          />
                        )}

                        {action.type === 'update_field' && (
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Field name"
                              value={action.field}
                              onChange={(e) => updateAction(index, 'field', e.target.value)}
                            />
                            <Input
                              placeholder="New value"
                              value={action.value}
                              onChange={(e) => updateAction(index, 'value', e.target.value)}
                            />
                          </div>
                        )}

                        {action.type === 'add_comment' && (
                          <Input
                            placeholder="Comment text"
                            value={action.message}
                            onChange={(e) => updateAction(index, 'message', e.target.value)}
                          />
                        )}
                      </div>

                      {actions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAction(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { resetForm(); setShowCreateDialog(false); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAutomation}
                className="bg-[#1C3E53] hover:bg-[#2A5470]"
                disabled={!automationName || !triggerType}
              >
                Create Automation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutomationsTab;
